/**
 * LanguageSettings Page
 * Manage supported languages and flag icons
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Languages as LanguagesIcon, Plus } from 'lucide-react';
import { useLanguages, useUpdateLanguages } from '../../services/language.service';
import { Language } from '../../types/admin';
import { LanguageRow } from '../../components/admin/LanguageRow';
import { EmptyState } from '../../components/admin/EmptyState';
import { AddLanguageDialog } from '../../components/admin/AddLanguageDialog';
import { useNotification } from '../../hooks/useNotification';

// ============================================
// LanguageSettings Component
// ============================================

export const LanguageSettings: React.FC = () => {
  const [localLanguages, setLocalLanguages] = useState<Language[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState<Language | null>(null);

  // Hooks
  const notification = useNotification();
  const { data: languages, isLoading, error } = useLanguages();
  const updateLanguagesMutation = useUpdateLanguages();

  // Initialize local state when data is fetched
  useEffect(() => {
    if (languages) {
      setLocalLanguages(languages);
    }
  }, [languages]);

  const persistLanguages = async (
    nextLanguages: Language[],
    flags: Record<string, File>,
    successMessage: string
  ) => {
    const previousLanguages = localLanguages;
    setLocalLanguages(nextLanguages);

    try {
      const savedLanguages = await updateLanguagesMutation.mutateAsync({
        languages: nextLanguages,
        flags,
      });

      setLocalLanguages(savedLanguages);
      notification.success(successMessage);
    } catch (error) {
      setLocalLanguages(previousLanguages);
      notification.error('Failed to update language settings. Please try again.');
      throw error;
    }
  };

  // Handle toggle active status
  const handleToggleActive = (key: string, active: boolean) => {
    const nextLanguages = localLanguages.map((lang) =>
      lang.key === key ? { ...lang, active } : lang
    );

    void persistLanguages(nextLanguages, {}, 'Language status updated successfully');
  };

  const handleSaveLanguage = async (language: Language, flagFile?: File) => {
    const isEdit = Boolean(editingLanguage);
    const nextLanguages = isEdit
      ? localLanguages.map((item) => (item.key === editingLanguage?.key ? language : item))
      : [...localLanguages, language];

    await persistLanguages(
      nextLanguages,
      flagFile ? { [language.key]: flagFile } : {},
      isEdit ? 'Language updated successfully' : 'Language added successfully'
    );
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLanguage(null);
  };

  return (
    <Box className="h-full bg-slate-50 p-8 dark:bg-slate-900">
      {/* Page Header */}
      <Box className="mb-6 flex items-start justify-between">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Language Settings
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Add and edit supported languages with their flag icons
          </Typography>
        </Box>
        <Box className="flex gap-2">
          <Button
            variant="outlined"
            startIcon={<Plus size={20} />}
            onClick={() => {
              setEditingLanguage(null);
              setDialogOpen(true);
            }}
            disabled={updateLanguagesMutation.isPending}
            sx={{
              borderColor: '#3B82F6',
              color: '#3B82F6',
              '&:hover': {
                borderColor: '#2563EB',
                backgroundColor: 'rgba(59, 130, 246, 0.04)',
              },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Add Language
          </Button>
        </Box>
      </Box>

      {/* Content Area */}
      {isLoading ? (
        // Loading State
        <Box className="flex h-[400px] items-center justify-center">
          <CircularProgress size={40} className="text-blue-500" />
        </Box>
      ) : error ? (
        // Error State
        <EmptyState
          Icon={LanguagesIcon}
          title="Failed to load languages"
          description="Please try again later"
        />
      ) : localLanguages.length === 0 ? (
        // Empty State
        <EmptyState
          Icon={LanguagesIcon}
          title="No languages available"
          description="No languages have been configured yet"
        />
      ) : (
        // Language Table
        <Paper
          elevation={0}
          className="rounded-xl bg-white dark:bg-slate-800"
          sx={{
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: '#f8fafc',
                    '& th': {
                      fontWeight: 600,
                      color: '#475569',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      letterSpacing: '0.05em',
                    },
                  }}
                >
                  <TableCell>Flag</TableCell>
                  <TableCell>Language Name</TableCell>
                  <TableCell>Keys</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localLanguages.map((language) => (
                  <LanguageRow
                    key={language.key}
                    language={language}
                    onToggleActive={handleToggleActive}
                    onEdit={(selectedLanguage) => {
                      setEditingLanguage(selectedLanguage);
                      setDialogOpen(true);
                    }}
                    disabled={updateLanguagesMutation.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add Language Dialog */}
      <AddLanguageDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveLanguage}
        existingKeys={localLanguages.flatMap((lang) => [lang.key, ...(lang.aliases ?? [])])}
        initialLanguage={editingLanguage}
        isSaving={updateLanguagesMutation.isPending}
      />
    </Box>
  );
};
